import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenEMSReport from "./HiddenEMSReport";
import SurveyInviteWidget from "./SurveyInviteWidget";

// ── Score → colour map (based on adjusted score ranges) ──────────────────────
const scoreColor = (val) => {
  if (val === null || val === undefined) return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  if (val >= 6)  return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // High   → green
  if (val >= 3)  return { bg: "#dbeafe", color: "#3f7cac", border: "#bfdbfe" }; // Medium → blue
  if (val >= 0)  return { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" }; // Low    → amber
  return          { bg: "#fee2e2", color: "#c2413b", border: "#fecaca" };        // Neg    → red
};

// ── Leadership style display config ──────────────────────────────────────────
const STYLES = [
  { key: "coercer",       label: "Coercer"   },
  { key: "pacesetter",    label: "Pacesetter" },
  { key: "authoritarian", label: "Auth."      },
  { key: "affiliator",    label: "Affiliator" },
  { key: "democrat",      label: "Democrat"   },
  { key: "coach",         label: "Coach"      },
];

// ── Motivational driver display config ───────────────────────────────────────
const DRIVERS = [
  { key: "power",       label: "Power"       },
  { key: "affiliation", label: "Affiliation" },
  { key: "achievement", label: "Achievement" },
];

export default function GlobalEMSResponses({ surveyId }) {
  const [emsData,       setEmsData]       = useState([]);
  const [loading,       setLoading]       = useState(true);

  // ── Filters ──
  const [filterName,      setFilterName]      = useState("");
  const [filterEmail,     setFilterEmail]     = useState("");
  const [filterCompany,   setFilterCompany]   = useState("");
  const [filterDept,      setFilterDept]      = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate,   setFilterEndDate]   = useState("");

  // ── PDF generation state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchEMSResults();
  }, [surveyId]);

  const fetchEMSResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score/ems/all`,
        { params: { survey_id: surveyId } }
      );
      setEmsData(res.data.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch EMS results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  const uniqueCompanies = [
    ...new Set(emsData.map(u => (u?.company || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueDepts = [
    ...new Set(emsData.map(u => (u?.department || "").trim()).filter(Boolean)),
  ].sort();

  const filteredData = emsData.filter((user) => {
    const name    = (user?.name       || "").toLowerCase();
    const email   = (user?.email      || "").toLowerCase();
    const company = (user?.company    || "").trim().toLowerCase();
    const dept    = (user?.department || "").trim().toLowerCase();

    const matchName    = name.includes(filterName.toLowerCase());
    const matchEmail   = email.includes(filterEmail.toLowerCase());
    const matchCompany = filterCompany === "" || company === filterCompany.toLowerCase();
    const matchDept    = filterDept    === "" || dept    === filterDept.toLowerCase();

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time);
      submissionDate.setHours(0, 0, 0, 0);

      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchDept && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterCompany("");
    setFilterDept("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);

    const ls = user.scores?.leadership_styles || {};
    const md = user.scores?.motivational_drivers || {};

    // Map API shape → the shape EMSResult / HiddenEMSReport expects
    const styleScores = {
      authoritative: ls.authoritarian?.adjusted ?? 0,
      coercer:       ls.coercer?.adjusted       ?? 0,
      democrat:      ls.democrat?.adjusted       ?? 0,
      affiliator:    ls.affiliator?.adjusted     ?? 0,
      pacesetter:    ls.pacesetter?.adjusted     ?? 0,
      coach:         ls.coach?.adjusted          ?? 0,
    };

    const driverScores = {
      power:       md.power       ?? 0,
      affiliation: md.affiliation ?? 0,
      achievement: md.achievement ?? 0,
    };

    setDownloadData({
      userInfo: {
        name:  user?.name  || "Participant",
        email: user?.email || "",
        phone: user?.phone || "",
      },
      styleScores,
      driverScores,
      insightText: "",   // HiddenEMSReport can generate its own or leave blank
      submission_time: user.submission_time,
    });
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading) return <div className="loading-state">Loading EMS data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>
       <SurveyInviteWidget surveyId={surveyId} />

      {/* Hidden PDF renderer */}
      <HiddenEMSReport
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h3 style={{ margin: 0 }}>🧭 EMS Leadership Styles ({filteredData.length})</h3>
        <span style={{
          background: "#A80D2C", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepts.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {emsData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No EMS responses found.</div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No responses match your current filters.</div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "280px" }}>
                  Leadership Styles (Adjusted)
                </th>
                <th style={{ padding: "12px", minWidth: "200px", background: "#fdf6e3" }}>
                  🎯 Motivational Drivers
                </th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const ls           = user.scores?.leadership_styles    || {};
                const md           = user.scores?.motivational_drivers || {};
                const isGenerating = isGeneratingRow === index;

                // Top style by adjusted score (for highlight)
                const topStyleKey = Object.entries(ls)
                  .sort((a, b) => (b[1]?.adjusted ?? -99) - (a[1]?.adjusted ?? -99))[0]?.[0];

                // Top driver
                const topDriverKey = Object.entries(md)
                  .sort((a, b) => (b[1] ?? -99) - (a[1] ?? -99))[0]?.[0];

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time
                        ? <>
                            {new Date(user.submission_time).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}<br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time).toLocaleTimeString("en-US", {
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{user?.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user?.email}<br />
                        {user?.phone || ""}
                      </span>
                    </td>

                    {/* ── Company / Dept ── */}
                    <td style={{ padding: "12px" }}>
                      {user?.company
                        ? <><b>{user.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user?.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.department}</span>
                        : null
                      }
                    </td>

                    {/* ── Leadership Style pills ── */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {STYLES.map(({ key, label }) => {
                          const styleData = ls[key];
                          const adjVal    = styleData?.adjusted ?? null;
                          const c         = scoreColor(adjVal);
                          const isTop     = key === topStyleKey;
                          return (
                            <span
                              key={key}
                              title={`Raw: ${styleData?.raw ?? "—"}  Adjusted: ${adjVal ?? "—"}`}
                              style={{
                                background:  c.bg,
                                color:       c.color,
                                border:      `${isTop ? "2px" : "1px"} solid ${c.border}`,
                                padding:     "2px 7px",
                                borderRadius: "4px",
                                fontSize:    "12px",
                                fontWeight:  isTop ? "800" : "600",
                                whiteSpace:  "nowrap",
                              }}
                            >
                              {label}: {adjVal ?? "—"}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    {/* ── Motivational Drivers ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {DRIVERS.map(({ key, label }) => {
                          const val  = md[key] ?? null;
                          const c    = scoreColor(val);
                          const isTop = key === topDriverKey;
                          return (
                            <span
                              key={key}
                              style={{
                                background:  c.bg,
                                color:       c.color,
                                border:      `${isTop ? "2px" : "1px"} solid ${c.border}`,
                                padding:     "2px 8px",
                                borderRadius: "4px",
                                fontSize:    "12px",
                                fontWeight:  isTop ? "800" : "600",
                                whiteSpace:  "nowrap",
                              }}
                            >
                              {label}: {val ?? "—"}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    {/* ── Download ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating}
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A80D2C",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}